/**
 * Renders the streamed answer, turning inline citation markers — `[S1]`, `[E2]`, or a combined
 * `[S1, S3]` — into chips that link to the citation's `href`. A marker the model wrote but that
 * never resolved to a real citation (the loop drops hallucinated handles before it ever reaches
 * `done.citations`, see api/src/wow/ask/citations.ts) renders as plain text, never a broken link.
 */
import { useNavigate } from 'react-router-dom';
import { MARKDOWN_PROSE } from '../library/MarkdownBody';
import { Marked } from 'marked';
import DOMPurify from 'dompurify';
import { cn } from '@/lib/utils';
import type { Citation } from './types';

/** Identical to the server's marker pattern (api/src/wow/ask/citations.ts) — one or more of
 * `S1`/`E2` separated by commas, inside a single pair of brackets. */
export const CITATION_MARKER_RE = /\[((?:[SE]\d+)(?:\s*,\s*[SE]\d+)*)\]/g;

export type AnswerSegment =
  | { kind: 'text'; text: string }
  | { kind: 'citation'; handle: string; citation: Citation };

/**
 * Pure: splits answer prose into text and citation segments. Exported so it can be unit-tested
 * without rendering — see Answer.test.ts.
 */
export function splitAnswerSegments(text: string, citations: Citation[]): AnswerSegment[] {
  const byHandle = new Map(citations.map((citation) => [citation.handle, citation] as const));
  const segments: AnswerSegment[] = [];

  const pushText = (value: string) => {
    if (!value) return;
    const last = segments[segments.length - 1];
    if (last && last.kind === TEXT) last.text += value;
    else segments.push({ kind: 'text', text: value });
  };

  const re = new RegExp(CITATION_MARKER_RE.source, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) pushText(text.slice(lastIndex, match.index));
    for (const rawHandle of (match[1] ?? '').split(',')) {
      const handle = rawHandle.trim();
      const citation = byHandle.get(handle);
      if (citation) segments.push({ kind: 'citation', handle, citation });
      else pushText(`[${handle}]`);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) pushText(text.slice(lastIndex));
  return segments;
}

const FENCE_RE = /^\s{0,3}(`{3,}|~{3,})(.*)$/;
const LIST_ITEM_RE = /^\s*(?:\d+[.)]|[-*+])\s+\S/;

/**
 * Pure: puts each step of an answer in its own block. "1. a 2. b" on one line is split at the next
 * number in sequence, and a list item that follows a prose line gets a blank line before it.
 */
export function normalizeAnswerMarkdown(text: string): string {
  const out: string[] = [];
  let fence: { char: string; size: number } | null = null;
  for (const line of text.split('\n')) {
    // A fenced code block is the author's own layout: its lines pass through untouched.
    const marker = FENCE_RE.exec(line);
    if (fence) {
      out.push(line);
      if (marker && marker[1]?.[0] === fence.char && (marker[1]?.length ?? 0) >= fence.size && !(marker[2] ?? '').trim()) fence = null;
      continue;
    }
    if (marker) {
      fence = { char: marker[1]?.[0] ?? '`', size: marker[1]?.length ?? 3 };
      out.push(line);
      continue;
    }
    const first = /^\s*(\d+)[.)]\s/.exec(line);
    let parts = [line];
    if (first) {
      let n = Number(first[1]);
      parts = [];
      let rest = line;
      for (;;) {
        const next = new RegExp(`\\s+(?=${n + 1}[.)]\\s+\\S)`).exec(rest);
        if (!next) break;
        parts.push(rest.slice(0, next.index));
        rest = rest.slice(next.index + next[0].length);
        n += 1;
      }
      parts.push(rest);
    }
    for (const part of parts) {
      const prev = out[out.length - 1];
      if (prev !== undefined && prev.trim() !== EMPTY && LIST_ITEM_RE.test(part) && (!LIST_ITEM_RE.test(prev) || parts.length > 1)) out.push('');
      out.push(part);
    }
  }
  return out.join('\n');
}

const EMPTY = '';
const ZERO_WIDTH = '\u200b';
const TEXT = 'text' as const;
const SECTION = 'section' as const;
const ENTITY = 'entity' as const;

const escapeHtml = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const CHIP_CLASS =
  'mx-0.5 inline-flex max-w-[14rem] items-center gap-1 rounded-full border border-border bg-secondary px-1.5 py-0.5 align-baseline text-[length:var(--t-eyebrow)] font-medium leading-4 no-underline text-secondary-foreground transition-colors duration-150 hover:bg-muted';

/** Resolved markers become `[S1](#cite-S1)` so the platform renderer emits them as links; unresolved ones stay plain text. */
function linkCitations(text: string, citations: Citation[]): string {
  let out = '';
  for (const s of splitAnswerSegments(text, citations)) {
    if (s.kind === TEXT) out += s.text;
    // "done![S1]" must not become a markdown image: a zero-width separator keeps "!" literal.
    else out += `${out.endsWith('!') ? ZERO_WIDTH : EMPTY}[${s.handle}](#cite-${s.handle})`;
  }
  return out;
}

/** The rendered `<a href="#cite-S1">S1</a>` becomes a chip link to the citation (text escaped; the sanitiser already ran). */
function chipHtml(html: string, citations: Citation[]): string {
  return html.replace(/<a href="#cite-([SE]\d+)"[^>]*>[^<]*<\/a>/g, (whole, handle: string) => {
    const c = citations.find((x) => x.handle === handle);
    if (!c) return whole;
    const [label, title] = c.kind === SECTION ? [c.headingPath, `${c.documentTitle} — ${c.headingPath}`] : [c.entityKey, `${c.entityType} — ${c.name}`];
    return `<a href="${escapeHtml(c.href)}" data-cite="${handle}" title="${escapeHtml(title)}" class="${CHIP_CLASS}"><span class="${c.kind === ENTITY ? 'font-mono' : 'truncate'}">${escapeHtml(label)}</span></a>`;
  });
}

const CITE_FRAGMENT_RE = /^#cite-[SE]\d+$/;

function pageOrigin(): string {
  return typeof location !== 'undefined' ? location.origin : 'http://localhost';
}

/**
 * The one decision for every link in an answer, made on the parsed URL (never a regex over the
 * text): a citation fragment, or a URL whose origin is this page's. Returns the href to emit, or
 * null when the link must go. The WHATWG parser reads `\` as `/`, so `/\attacker.example` resolves
 * to another origin here and is refused, exactly as the browser would follow it.
 */
function safeHref(href: string): string | null {
  const raw = href.trim();
  if (CITE_FRAGMENT_RE.test(raw)) return raw;
  let url: URL;
  try {
    url = new URL(raw, pageOrigin());
  } catch {
    return null;
  }
  if (url.origin !== pageOrigin()) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Model output is untrusted. Raw HTML is switched off at the token level (it is shown as escaped
 * text, never parsed), a markdown image keeps only its alt text, and a link survives only when
 * `safeHref` accepts it; otherwise its text stays and the link goes.
 */
const answerMarked = new Marked({
  gfm: true,
  breaks: false,
  renderer: {
    html: ({ text }) => escapeHtml(text),
    image: ({ text }) => escapeHtml(text),
    link(token) {
      const inner = this.parser.parseInline(token.tokens);
      const href = safeHref(token.href);
      if (href === null) return inner;
      return CITE_FRAGMENT_RE.test(href) ? `<a href="${href}">${inner}</a>` : `<a href="${escapeHtml(href)}" rel="noopener">${inner}</a>`;
    },
  },
});

const LOADING_TAGS = 'img,picture,source,video,audio,track,iframe,embed,object,input,svg,link,meta,style,script,form,base,frame,frameset,applet';
const LOADING_ATTRS = ['src', 'srcset', 'poster', 'background', 'ping', 'formaction', 'style', 'data', 'action'];

/**
 * Defence in depth after the sanitiser, on a parsed document (DOMParser), never regexes: every
 * element that can load a resource goes, every attribute that can fetch or restyle goes, and each
 * `<a>` is re-checked by its real `href`. Without a DOM (unit tests) the token-level rules above
 * are the whole guarantee.
 */
export function restrictAnswerHtml(html: string): string {
  if (typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const el of Array.from(doc.body.querySelectorAll(LOADING_TAGS))) el.remove();
  for (const el of Array.from(doc.body.querySelectorAll('*'))) {
    for (const name of el.getAttributeNames()) {
      if (LOADING_ATTRS.includes(name.toLowerCase()) || name.toLowerCase().startsWith('on')) el.removeAttribute(name);
    }
  }
  for (const a of Array.from(doc.body.querySelectorAll('a'))) {
    const href = safeHref(a.getAttribute('href') ?? '');
    if (href === null) {
      a.replaceWith(...Array.from(a.childNodes));
      continue;
    }
    a.setAttribute('href', href);
    a.removeAttribute('target');
    if (!CITE_FRAGMENT_RE.test(href)) a.setAttribute('rel', 'noopener');
  }
  return doc.body.innerHTML;
}

function sanitizeAnswer(html: string): string {
  if (typeof DOMPurify.sanitize !== 'function' || DOMPurify.isSupported === false) return html;
  return DOMPurify.sanitize(html, {
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['srcdoc', 'style', 'formaction'],
    ALLOWED_URI_REGEXP: /^(?:#|\/)/,
    // the regex is tested against every attribute not on DOMPurify's URI-safe list; these two are not links
    ADD_URI_SAFE_ATTR: ['start', 'align'],
  });
}

export function renderAnswerHtml(text: string, citations: Citation[]): string {
  const raw = answerMarked.parse(linkCitations(normalizeAnswerMarkdown(text), citations), { async: false }) as string;
  return chipHtml(restrictAnswerHtml(sanitizeAnswer(raw)), citations);
}

export function Answer({ text, citations }: { text: string; citations: Citation[] }) {
  const navigate = useNavigate();
  if (!text) return null;
  const html = renderAnswerHtml(text, citations);
  return (
    <div
      data-testid="ask-answer-body"
      data-kit="prose"
      className={cn(MARKDOWN_PROSE, 'text-foreground')}
      onClick={(e) => {
        const a = (e.target as HTMLElement).closest('a[data-cite]');
        if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(a.getAttribute('href') ?? '/');
      }}
      // answerMarked (no raw HTML), DOMPurify and restrictAnswerHtml are the only path in; the chips are built from escaped citation fields.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
